import { useState, useCallback, useEffect } from "react";

const STORAGE_PREFIX = "hint:dismissed:";

export interface UseDismissableHintResult {
  /** Whether the hint has been dismissed */
  isDismissed: boolean;
  /** Call this to dismiss the hint permanently */
  dismiss: () => void;
  /** Call this to reset the hint (show it again) */
  reset: () => void;
}

/**
 * Hook for managing dismissable hints with localStorage persistence.
 *
 * Use this when you want a hint to be shown until the user performs
 * a specific action, then stay hidden on future visits.
 *
 * @param storageKey - Unique key for storing the dismissed state.
 *                     If undefined, the hint is never auto-dismissed.
 *
 * @example
 * ```tsx
 * const { isDismissed, dismiss } = useDismissableHint("floorplan-rotation");
 *
 * // Show hint only if not dismissed
 * <PointingHint visible={!isDismissed}>
 *   Try rotating the floor plan!
 * </PointingHint>
 *
 * // Dismiss when user performs the action
 * <button onClick={() => { rotateFloorplan(); dismiss(); }}>
 *   Rotate
 * </button>
 * ```
 */
export function useDismissableHint(
  storageKey: string | undefined,
): UseDismissableHintResult {
  const [isDismissed, setIsDismissed] = useState<boolean>(() => {
    if (!storageKey) return false;

    try {
      const stored = localStorage.getItem(STORAGE_PREFIX + storageKey);
      return stored === "true";
    } catch {
      // localStorage might not be available (SSR, private browsing, etc.)
      return false;
    }
  });

  // Sync state with localStorage on mount (handles SSR hydration)
  useEffect(() => {
    if (!storageKey) return;

    try {
      const stored = localStorage.getItem(STORAGE_PREFIX + storageKey);
      if (stored === "true" && !isDismissed) {
        setIsDismissed(true);
      }
    } catch {
      // Ignore localStorage errors
    }
  }, [storageKey, isDismissed]);

  const dismiss = useCallback(() => {
    if (!storageKey) return;

    setIsDismissed(true);
    try {
      localStorage.setItem(STORAGE_PREFIX + storageKey, "true");
    } catch {
      // Ignore localStorage errors
    }
  }, [storageKey]);

  const reset = useCallback(() => {
    if (!storageKey) return;

    setIsDismissed(false);
    try {
      localStorage.removeItem(STORAGE_PREFIX + storageKey);
    } catch {
      // Ignore localStorage errors
    }
  }, [storageKey]);

  return { isDismissed, dismiss, reset };
}

/**
 * Utility to reset all dismissed hints.
 * Useful for development or user settings.
 */
export function resetAllDismissedHints(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Ignore localStorage errors
  }
}

export default useDismissableHint;
